import { Box } from "@mui/material"
import type { FC } from "react"
import { Controller, useFormContext } from "react-hook-form"

import { SingleFileUpload } from "@/components/upload/SingleFileUpload"
import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES } from "@/constants/forms"

const ALLOWED_IMAGE_TYPES = ["image/jpg", "image/png", "image/gif", "image/jpeg"]
const ALLOWED_FILE_SIZE = 10 * 1024 * 1024 // 10MB

interface UploadInstructionProps {
  title: string
  onChange?: () => void
  name: string
  fileData?: File
}

const UploadInstruction: FC<UploadInstructionProps> = ({ title, name, fileData, onChange }) => {
  const { control, resetField, setValue, setError } = useFormContext()

  const validateFileUpload = (file: File | null) => {
    if (file && file?.size >= ALLOWED_FILE_SIZE) {
      setError(name, {
        type: "fileSize",
        message: "Max allowed file size 10MB",
      })
      return false
    }

    if (file && !ALLOWED_IMAGE_TYPES.includes(file?.type)) {
      setError(name, {
        type: "fileType",
        message: "Allowed file type should be JPG, JPEG, PNG or GIF",
      })
      return false
    }

    return true
  }

  const handleFileChange = (newFile: File | null) => {
    resetField(name)
    if (validateFileUpload(newFile)) {
      setValue(name, newFile ?? undefined)
      onChange?.()
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box className='mt-4' data-testid={`instructions-form-${name}`}>
          <label htmlFor={name} className={FIELD_LABEL_CLASSES}>
            {title}
          </label>
          <SingleFileUpload
            accept='.jpg,.jpeg,.png,.gif'
            title='Click to upload or drag and drop'
            subtitle='JPG, JPEG, PNG or GIF (max. 10MB)'
            onChange={handleFileChange}
            file={fileData || field.value}
          />
          {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
        </Box>
      )}
    />
  )
}

export default UploadInstruction
